import { Injectable, Optional } from '@angular/core';
import { INDDI18nServiceConfigResources, NDDI18nServiceConfigResources, NDDTranslationResourceService } from '@ndk/ng-translation/component';

/**
 * Interface com atributos necessários para configuração i18n.
 *
 * @export
 * @interface INDDEditorI18nServiceConfigResources
 * @extends {INDDI18nServiceConfigResources}
 */
export interface INDDEditorI18nServiceConfigResources extends INDDI18nServiceConfigResources {
    // Editor
    accessibilityTab?: string,
    addColumnLeft?: string,
    addColumnRight?: string,
    addRowAbove?: string,
    addRowBelow?: string,
    alignCenter?: string,
    alignCenterBottom?: string,
    alignCenterMiddle?: string,
    alignCenterTop?: string,
    alignLeft?: string,
    alignLeftBottom?: string,
    alignLeftMiddle?: string,
    alignLeftTop?: string,
    alignRemove?: string,
    alignRight?: string,
    alignRightBottom?: string,
    alignRightMiddle?: string,
    alignRightTop?: string,
    alignment?: string,
    associateCellsWithHeaders?: string,
    backColor?: string,
    background?: string,
    bold?: string,
    border?: string,
    caption?: string,
    cellMargin?: string,
    cellPadding?: string,
    cellSpacing?: string,
    cellTab?: string,
    cleanFormatting?: string,
    collapseBorders?: string,
    columns?: string,
    createLink?: string,
    createTable?: string,
    createTableHint?: string,
    cssClass?: string,
    deleteColumn?: string,
    deleteRow?: string,
    dialogCancel?: string,
    dialogInsert?: string,
    dialogOk?: string,
    dialogUpdate?: string,
    editAreaTitle?: string,
    fileTitle?: string,
    fileWebAddress?: string,
    fontName?: string,
    fontNameInherit?: string,
    fontSize?: string,
    fontSizeInherit?: string,
    foreColor?: string,
    formatting?: string,
    height?: string,
    id?: string,
    imageAltText?: string,
    imageHeight?: string,
    imageWebAddress?: string,
    imageWidth?: string,
    indent?: string,
    insertFile?: string,
    insertHtml?: string,
    insertImage?: string,
    insertOrderedList?: string,
    insertUnorderedList?: string,
    italic?: string,
    justifyCenter?: string,
    justifyFull?: string,
    justifyLeft?: string,
    justifyRight?: string,
    linkOpenInNewWindow?: string,
    linkText?: string,
    linkToolTip?: string,
    linkWebAddress?: string,
    outdent?: string,
    overflowAnchor?: string,
    print?: string,
    rows?: string,
    selectAllCells?: string,
    strikethrough?: string,
    subscript?: string,
    summary?: string,
    superscript?: string,
    tableTab?: string,
    tableWizard?: string,
    underline?: string,
    units?: string,
    unlink?: string,
    viewHtml?: string,
    width?: string,
    wrapText?: string,

    // Image/File Browser
    uploadFile?: string,
    orderBy?: string,
    orderByName?: string,
    orderBySize?: string,
    directoryNotFound?: string,
    emptyFolder?: string,
    deleteFile?: string,
    invalidFileType?: string,
    overwriteFile?: string,
    search?: string,
}

/**
 * Configurações i18n gerais do módulo.
 *
 * @export
 * @class NDDEditorI18nServiceConfig
 * @implements {NDDI18nServiceConfigResources}
 */
export class NDDEditorI18nServiceConfig implements NDDI18nServiceConfigResources {
    public i18n: boolean;
    public resources: INDDEditorI18nServiceConfigResources = {
        // TODO: Verificar traduções em https://github.com/telerik/kendo-ui-core/blob/master/src/messages/kendo.messages.pt-BR.js
        // Editor
        accessibilityTab: 'Acessibilidade',
        addColumnLeft: 'Adicionar coluna à esquerda',
        addColumnRight: 'Adicionar coluna à direita',
        addRowAbove: 'Adicionar linha acima',
        addRowBelow: 'Adicionar linha abaixo',
        alignCenter: 'Centralizado',
        alignCenterBottom: 'Centralizado inferior',
        alignCenterMiddle: 'Centralizado',
        alignCenterTop: 'Centralizado superior',
        alignLeft: 'À esquerda',
        alignLeftBottom: 'À esquerda inferior',
        alignLeftMiddle: 'À esquerda',
        alignLeftTop: 'À esquerda superior',
        alignRemove: 'Remover alinhamento',
        alignRight: 'À direita',
        alignRightBottom: 'À direita inferior',
        alignRightMiddle: 'À direita',
        alignRightTop: 'À direita superior',
        alignment: 'Alinhamento',
        associateCellsWithHeaders: 'Associar células com cabeçalhos',
        backColor: 'Cor de fundo',
        background: 'Fundo',
        bold: 'Negrito',
        border: 'Borda',
        caption: 'Legenda',
        cellMargin: 'Margem externa',
        cellPadding: 'Margem interna',
        cellSpacing: 'Espaçamento',
        cellTab: 'Célula',
        cleanFormatting: 'Limpar formatação',
        collapseBorders: 'Ocultar bordas',
        columns: 'Colunas',
        createLink: 'Inserir hiperlink',
        createTable: 'Criar tabela',
        createTableHint: 'Criar tabela {0} x {1}',
        cssClass: 'Classe CSS',
        deleteColumn: 'Excluir coluna',
        deleteRow: 'Excluir linha',
        dialogCancel: 'Cancelar',
        dialogInsert: 'Inserir',
        dialogOk: 'OK',
        dialogUpdate: 'Atualizar',
        editAreaTitle: 'Escreva seus comentários aqui. Pressione F10 para a barra de ferramentas.',
        fileTitle: 'Título',
        fileWebAddress: 'Endereço web',
        fontName: 'Selecionar fonte',
        fontNameInherit: '(fonte padrão)',
        fontSize: 'Selecionar tamanho da fonte',
        fontSizeInherit: '(tamanho padrão)',
        foreColor: 'Cor',
        formatting: 'Formatar',
        height: 'Altura',
        id: 'ID',
        imageAltText: 'Texto alternativo',
        imageHeight: 'Altura (px)',
        imageWebAddress: 'Endereço web',
        imageWidth: 'Largura (px)',
        indent: 'Aumentar recuo',
        insertFile: 'Inserir arquivo',
        insertHtml: 'Inserir HTML',
        insertImage: 'Inserir imagem',
        insertOrderedList: 'Inserir lista ordenada',
        insertUnorderedList: 'Inserir lista',
        italic: 'Itálico',
        justifyCenter: 'Centralizar',
        justifyFull: 'Justificar',
        justifyLeft: 'Alinhar à esquerda',
        justifyRight: 'Alinhar à direita',
        linkOpenInNewWindow: 'Abrir link em nova janela',
        linkText: 'Texto',
        linkToolTip: 'Tooltip',
        linkWebAddress: 'Endereço web',
        outdent: 'Diminuir recuo',
        overflowAnchor: 'Mais ferramentas',
        print: 'Imprimir',
        rows: 'Linhas',
        selectAllCells: 'Selecionar todas as células',
        strikethrough: 'Tachado',
        subscript: 'Subscrito',
        summary: 'Resumo',
        superscript: 'Sobrescrito',
        tableTab: 'Table',
        tableWizard: 'Configurar tabela',
        underline: 'Sublinhado',
        units: 'Unidades',
        unlink: 'Remover hiperlink',
        viewHtml: 'Ver HTML',
        width: 'Largura',
        wrapText: 'Wrap text',
        // Image/File Browser
        uploadFile: 'Enviar arquivo',
        orderBy: 'Ordenar por',
        orderByName: 'Nome do arquivo',
        orderBySize: 'Tamanho do arquivo',
        directoryNotFound: 'Diretório não encontrado!',
        emptyFolder: 'Pasta vazia',
        deleteFile: 'Deseja realmente excluir {0}? Essa ação não pode ser desfeita.',
        invalidFileType: 'O arquivo selecionado "{0}" não é válido. Os tipos de arquivo suportados são {1}.',
        overwriteFile: 'Um arquivo com o nome "{0}" já existe no diretório atual. Deseja sobrescrevê-lo?',
        search: 'Pesquisar',
    };
}

/** Serviço de configuração i18n. */
@Injectable()
export class NDDEditorI18nService extends NDDTranslationResourceService {
    constructor(@Optional() config: NDDEditorI18nServiceConfig) {
        super(config, new NDDEditorI18nServiceConfig());
    }
}
